import generateUniqueId from '../../../util/generateUniqueId';
import { areSortedArraysEqual, unique } from '../../../util/iteratees';
import {
  getWalletCollectionTabOrderId,
  REQUIRED_SYSTEM_WALLET_COLLECTION_TAB_IDS,
  SYSTEM_WALLET_COLLECTION_TAB_IDS,
} from '../../../util/walletCollectionOrder';
import { addActionHandler } from '../../index';
import { updateAuth, updateSettings } from '../../reducers';
import { selectNetworkAccounts } from '../../selectors';

addActionHandler('openAccountSelector', (global) => {
  global = updateAuth(global, {
    forceAddingTonOnlyAccount: undefined,
    initialAddAccountState: undefined,
    shouldHideAddAccountBackButton: undefined,
    pendingWalletCollectionId: undefined,
  });

  return { ...global, isAccountSelectorOpen: true };
});

addActionHandler('closeAccountSelector', (global) => {
  global = updateAuth(global, {
    forceAddingTonOnlyAccount: undefined,
    initialAddAccountState: undefined,
    shouldHideAddAccountBackButton: undefined,
    pendingWalletCollectionId: undefined,
  });

  return { ...global, isAccountSelectorOpen: undefined };
});

addActionHandler('openWalletRenameModal', (global, actions, payload) => {
  const accountId = payload?.accountId ?? global.currentAccountId;

  if (!accountId) {
    return undefined;
  }

  return { ...global, walletRenameAccountId: accountId };
});

addActionHandler('closeWalletRenameModal', (global) => {
  return { ...global, walletRenameAccountId: undefined };
});

addActionHandler('setAccountSelectorTab', (global, actions, { tab }) => {
  return { ...global, accountSelectorActiveTab: tab };
});

addActionHandler('setAccountSelectorViewMode', (global, actions, { mode }) => {
  return { ...global, accountSelectorViewMode: mode };
});

addActionHandler('setPendingWalletCollection', (global, actions, { collectionId } = {}) => {
  return updateAuth(global, { pendingWalletCollectionId: collectionId });
});

addActionHandler('saveWalletCollection', (global, actions, payload) => {
  const name = payload.name.trim().slice(0, 40);
  if (!name) return global;

  const validAccountIds = new Set(Object.keys(global.accounts?.byId ?? {}));
  const accountIds = unique(payload.accountIds).filter((accountId) => validAccountIds.has(accountId));
  if (!accountIds.length) return global;

  const walletCollections = global.settings.walletCollections ?? [];
  const existingIndex = payload.id
    ? walletCollections.findIndex(({ id }) => id === payload.id)
    : -1;
  const collection = {
    id: existingIndex >= 0 ? walletCollections[existingIndex].id : generateUniqueId(),
    name,
    network: payload.network,
    accountIds,
  };
  const nextCollections = [...walletCollections];

  if (existingIndex >= 0) {
    nextCollections[existingIndex] = collection;
  } else {
    nextCollections.push(collection);
  }

  return updateSettings(global, { walletCollections: nextCollections });
});

addActionHandler('deleteWalletCollection', (global, actions, { id }) => {
  const walletCollections = global.settings.walletCollections?.filter((collection) => collection.id !== id);

  return updateSettings(global, {
    walletCollections: walletCollections?.length ? walletCollections : undefined,
  });
});

addActionHandler('reorderWalletCollectionTabs', (global, actions, { network, orderedIds }) => {
  const networkCollections = (global.settings.walletCollections ?? []).filter(
    (collection) => collection.network === network,
  );
  const orderedIdSet = new Set(orderedIds);
  if (orderedIdSet.size !== orderedIds.length || !orderedIds.length) return global;

  const collectionOrderIds = networkCollections.map(({ id }) => getWalletCollectionTabOrderId(id));
  const allowedIds = new Set([
    ...Object.values(SYSTEM_WALLET_COLLECTION_TAB_IDS),
    ...collectionOrderIds,
  ]);
  if (orderedIds.some((id) => !allowedIds.has(id))) return global;
  if (REQUIRED_SYSTEM_WALLET_COLLECTION_TAB_IDS.some((id) => !orderedIdSet.has(id))) return global;
  if (collectionOrderIds.some((id) => !orderedIdSet.has(id))) return global;

  const currentOrder = global.settings.walletCollectionTabOrder?.[network];
  if (currentOrder && areSortedArraysEqual(currentOrder, orderedIds)) return global;

  return updateSettings(global, {
    walletCollectionTabOrder: {
      ...global.settings.walletCollectionTabOrder,
      [network]: orderedIds,
    },
  });
});

addActionHandler('rebuildOrderedAccountIds', (global) => {
  const { orderedAccountIds = [] } = global.settings;
  const accounts = selectNetworkAccounts(global);
  const allAccountIds = accounts ? Object.keys(accounts) : [];
  const newOrderedAccountIds = unique([...orderedAccountIds, ...allAccountIds]);

  if (areSortedArraysEqual(orderedAccountIds, newOrderedAccountIds)) {
    return global;
  }

  return updateSettings(global, {
    orderedAccountIds: newOrderedAccountIds,
  });
});

addActionHandler('updateOrderedAccountIds', (global, actions, { orderedAccountIds }) => {
  return updateSettings(global, {
    orderedAccountIds,
  });
});
